/**
 * NIP-44 Encryption Service
 * Supports both browser extensions and seed-based encryption
 * 
 * NIP-44 is more secure than NIP-04 with:
 * - XChaCha20-Poly1305 encryption
 * - Padding to hide message length
 * - Better key derivation (HKDF)
 */
import * as nip44 from 'nostr-tools/nip44'

export const nip44Service = {
    /**
     * Check if NIP-44 is supported
     */
    isSupported(method: 'extension' | 'seed'): boolean {
        if (method === 'extension') {
            return !!(window.nostr?.nip44);
        }
        // Seed-based NIP-44 is now supported
        return true;
    },

    /**
     * Get conversation key for seed-based encryption
     * Uses NIP-44 key derivation
     */
    getConversationKey(sk: Uint8Array, recipientPubkey: string): Uint8Array {
        return nip44.v2.utils.getConversationKey(sk, recipientPubkey);
    },

    /**
     * Encrypt message for a recipient using NIP-44
     * Supports both extension and seed-based methods
     */
    async encrypt(
        content: string,
        recipientPubkey: string,
        method: 'extension' | 'seed',
        sk?: Uint8Array
    ): Promise<string> {
        try {
            if (method === 'extension') {
                if (!window.nostr?.nip44) {
                    throw new Error('Nostr extension does not support NIP-44');
                }
                return await window.nostr.nip44.encrypt(recipientPubkey, content);
            } else {
                // Seed-based NIP-44 encryption
                if (!sk) {
                    throw new Error('Private key required for seed-based NIP-44 encryption');
                }
                
                const conversationKey = this.getConversationKey(sk, recipientPubkey);
                return nip44.v2.encrypt(content, conversationKey);
            }
        } catch (error) {
            console.error('❌ [NIP44Service] Encryption failed:', error);
            throw error;
        }
    },

    /**
     * Decrypt message from a sender using NIP-44
     * Supports both extension and seed-based methods
     */
    async decrypt(
        ciphertext: string,
        senderPubkey: string,
        method: 'extension' | 'seed',
        sk?: Uint8Array
    ): Promise<string> {
        try {
            if (method === 'extension') {
                if (!window.nostr?.nip44) {
                    throw new Error('Nostr extension does not support NIP-44');
                }
                return await window.nostr.nip44.decrypt(senderPubkey, ciphertext);
            } else {
                // Seed-based NIP-44 decryption
                if (!sk) {
                    throw new Error('Private key required for seed-based NIP-44 decryption');
                }
                
                const conversationKey = this.getConversationKey(sk, senderPubkey);
                return nip44.v2.decrypt(ciphertext, conversationKey);
            }
        } catch (error) {
            console.warn(`⚠️ [NIP44Service] Decryption failed for ${senderPubkey.slice(0, 8)}...`);
            throw error;
        }
    },

    /**
     * Check if a ciphertext is NIP-44 format
     * NIP-44 ciphertexts are base64 and longer due to padding
     */
    isNip44Ciphertext(ciphertext: string): boolean {
        try {
            // NIP-44 uses versioned format - first byte is version (2 for v2)
            const decoded = atob(ciphertext);
            // Version 2 is the current NIP-44 version
            return decoded.charCodeAt(0) === 2;
        } catch {
            return false;
        }
    }
};




